"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Book, Coins } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

const pentaclesCards = [
  { name: "Ace of Pentacles", slug: "ace-of-pentacles", turkish: "Pentagramlar Ası", keyword: "Yeni fırsatlar", image: "ace-of-pentacles.svg" },
  { name: "Two of Pentacles", slug: "two-of-pentacles", turkish: "İki Pentagram", keyword: "Denge ve çok görevlilik", image: "two-of-pentacles.svg" },
  { name: "Three of Pentacles", slug: "three-of-pentacles", turkish: "Üç Pentagram", keyword: "İşbirliği ve ustalık", image: "three-of-pentacles.svg" },
  { name: "Four of Pentacles", slug: "four-of-pentacles", turkish: "Dört Pentagram", keyword: "Güvenlik ve kontrol", image: "four-of-pentacles.svg" },
  { name: "Five of Pentacles", slug: "five-of-pentacles", turkish: "Beş Pentagram", keyword: "Maddi zorluk", image: "five-of-pentacles.svg" },
  { name: "Six of Pentacles", slug: "six-of-pentacles", turkish: "Altı Pentagram", keyword: "Cömertlik ve paylaşım", image: "six-of-pentacles.svg" },
  { name: "Seven of Pentacles", slug: "seven-of-pentacles", turkish: "Yedi Pentagram", keyword: "Sabır ve değerlendirme", image: "seven-of-pentacles.svg" },
  { name: "Eight of Pentacles", slug: "eight-of-pentacles", turkish: "Sekiz Pentagram", keyword: "Ustalık ve özveri", image: "eight-of-pentacles.svg" },
  { name: "Nine of Pentacles", slug: "nine-of-pentacles", turkish: "Dokuz Pentagram", keyword: "Maddi başarı", image: "nine-of-pentacles.svg" },
  { name: "Ten of Pentacles", slug: "ten-of-pentacles", turkish: "On Pentagram", keyword: "Kalıcı zenginlik", image: "ten-of-pentacles.svg" },
  { name: "Page of Pentacles", slug: "page-of-pentacles", turkish: "Pentagramlar Prensi", keyword: "Öğrenme ve fırsat", image: "page-of-pentacles.svg" },
  { name: "Knight of Pentacles", slug: "knight-of-pentacles", turkish: "Pentagramlar Şövalyesi", keyword: "Kararlılık ve çalışkanlık", image: "knight-of-pentacles.svg" },
  { name: "Queen of Pentacles", slug: "queen-of-pentacles", turkish: "Pentagramlar Kraliçesi", keyword: "Pratik bilgelik", image: "queen-of-pentacles.svg" },
  { name: "King of Pentacles", slug: "king-of-pentacles", turkish: "Pentagramlar Kralı", keyword: "Maddi güç", image: "king-of-pentacles.svg" }
];

export default function PentaclesSuitPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-green-300">
            <Book className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Pentagramlar</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot Sistemleri
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Minör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Pentagramlar</span>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-xl p-8 mb-8 border border-green-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Coins className="h-10 w-10 text-green-400" />
            Pentagramlar (Pentacles) Suit'i
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Toprak elementi ile ilişkilendirilen Pentagramlar suit'i, maddi dünya, para, kariyer ve
                pratik işler alanlarını temsil eder. Zenginlik, sağlık ve somut başarılar konularında rehberlik sunar.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Toprak Elementi</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Para</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Kariyer</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Sağlık</span>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-6 border border-green-500/30">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Temel Özellikler</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• <strong>Element:</strong> Toprak</li>
                <li>• <strong>Kişilik:</strong> Melancholic (Soğuk ve Kuru)</li>
                <li>• <strong>Astrolojik İlişki:</strong> Boğa, Başak, Oğlak</li>
                <li>• <strong>Yaşam Alanı:</strong> İş, para, sağlık, maddi güvenlik</li>
                <li>• <strong>Anahtar Kelimeler:</strong> İstikrar, bolluk, pratiklik</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {pentaclesCards.map((card, index) => (
            <Link
              key={card.slug}
              href={`/about/tarot/systems/rider-waite/minor-arcana/pentacles/${card.slug}`}
              className="block"
            >
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-green-500/50 transition-all duration-300 cursor-pointer group shadow-lg hover:shadow-xl hover:scale-[1.02] h-full">
                <div className="text-center">
                  <div className="w-24 h-36 mx-auto mb-4 rounded-lg overflow-hidden group-hover:scale-105 transition-transform duration-300 shadow-lg">
                    <img
                      src={`/images/tarot/cards/${card.image}`}
                      alt={card.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to numbered circle if image fails to load
                        e.currentTarget.style.display = 'none';
                        const sibling = e.currentTarget.nextElementSibling as HTMLElement;
        if (sibling) {
          sibling.style.display = 'flex';
        }
                      }}
                    />
                    <div className="w-full h-full bg-gradient-to-br from-green-500 to-emerald-700 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                      <span className="text-white font-bold text-lg">{index + 1}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-100 mb-2">{card.name}</h3>
                  <p className="text-slate-400 text-sm mb-3">{card.turkish}</p>
                  <p className="text-green-400 text-sm font-medium">{card.keyword}</p>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <span className="text-green-400 text-sm group-hover:translate-x-1 transition-transform duration-300 inline-block">
                    Kartı İncele →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Suit Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4">Pentagramlar Suit'inin Anlamı</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-2">Pozitif Yönler</h3>
                <ul className="space-y-1 text-slate-300 text-sm">
                  <li>• Maddi güvenlik ve bolluk</li>
                  <li>• Pratik zeka ve işe yarar çözümler</li>
                  <li>• İstikrarlı büyüme ve ilerleme</li>
                  <li>• Sağlık ve vitalite</li>
                  <li>• Çalışkanlık ve özveri</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-2">Dikkat Edilmesi Gerekenler</h3>
                <ul className="space-y-1 text-slate-300 text-sm">
                  <li>• Aşırı materializmm ve açgözlülük</li>
                  <li>• Rutine takılma ve değişime direnc</li>
                  <li>• Cimrilik ve paylaşmama</li>
                  <li>• Sadece maddi değerlere odaklanma</li>
                  <li>• Aşırı temkinlilik ve risk almama</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4">Yaşam Alanlarındaki Etkiler</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-green-400 mb-2">İş ve Kariyer</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Maddi kazanç, yatırımlar, iş fırsatları ve uzun vadeli kariyer planları.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-400 mb-2">Sağlık</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Fiziksel sağlık, beslenme, egzersiz ve bedensel iyileşme süreçleri.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-400 mb-2">Ev ve Aile</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Ev alımı, aile güvenliği, pratik yaşam düzenlemeleri ve günlük rutinler.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Element Connection */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Toprak Elementi Bağlantısı</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Coins className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İstikrar</h3>
              <p className="text-slate-400 text-sm">Güvenli temel, dayanıklılık ve süreklilik</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🌱</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Büyüme</h3>
              <p className="text-slate-400 text-sm">Doğal gelişim, sabırla ilerleme</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🏠</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Güvenlik</h3>
              <p className="text-slate-400 text-sm">Maddi güvence ve koruma</p>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Tarot kartları sembolik yorumlama araçlarıdır ve kesinlik iddiası taşımazlar. İçerikler tamamen
            merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/swords"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kılıçlar
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            Minör Arkana
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}